import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Beaker, Printer, Settings as SettingsIcon, Coins, AlertTriangle, History as HistoryIcon, Receipt, Clock, ClipboardList, ShoppingCart } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ liquids: 0 });
  const [dluo, setDluo] = useState({ alerts: [], count: 0, expired_count: 0 });

  useEffect(() => {
    api.get("/liquids").then(({ data }) => setStats({ liquids: data.length })).catch(() => {});
    api.get("/alerts/dluo?days=60").then(({ data }) => setDluo(data)).catch(() => {});
  }, []);

  const isAdmin = user?.role === "admin";
  const credits = user?.credits ?? 0;
  const lowCredits = !isAdmin && credits < 10;
  const noCredits = !isAdmin && credits === 0;

  return (
    <DashboardLayout title={`Bienvenue, ${user?.shop_name || ""}`}>
      {noCredits && (
        <div data-testid="dashboard-no-credits-banner" className="mb-6 border border-red-500/40 bg-red-500/5 p-5 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-display font-semibold text-white">Solde à zéro</div>
            <div className="text-sm text-zinc-400 mt-1">
              Vous n'avez plus de crédits d'impression. Rechargez à partir de 30€ pour continuer à imprimer.
            </div>
          </div>
          <Link
            to="/credits"
            data-testid="dashboard-recharge-cta"
            className="text-sm bg-red-500 hover:bg-red-600 text-white px-4 py-2 font-medium inline-flex items-center gap-2"
          >
            <ShoppingCart className="w-4 h-4" /> Recharger
          </Link>
        </div>
      )}
      {lowCredits && !noCredits && (
        <div data-testid="dashboard-low-credits-banner" className="mb-6 border border-orange-500/40 bg-orange-500/5 p-4 flex items-center justify-between gap-3">
          <div className="text-sm text-zinc-300">
            Solde bas : <span className="font-bold text-white">{credits}</span> crédit{credits > 1 ? "s" : ""} restant{credits > 1 ? "s" : ""}. Pensez à recharger.
          </div>
          <Link to="/credits" className="text-xs bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 font-medium">
            Recharger
          </Link>
        </div>
      )}

      <div className="grid grid-cols-12 gap-0 border border-zinc-800 fade-stagger">
        <StatCell label="Liquides catalogués" value={stats.liquids} accent="red" testid="stat-liquids" />
        <StatCell label="Formats de flacons" value="5" accent="white" testid="stat-formats" />
        <StatCell label="Crédits" value={isAdmin ? "∞" : credits} accent={noCredits ? "red" : lowCredits ? "orange" : "green"} testid="stat-credits" />
      </div>

      <div className="grid grid-cols-12 gap-0 border-l border-r border-b border-zinc-800">
        <ActionCell to="/liquids" testid="quick-liquids" icon={Beaker} label="Bibliothèque de liquides" desc="Ajoutez, importez CSV ou modifiez vos parfums." />
        <ActionCell to="/generate" testid="quick-generate" icon={Printer} label="Générer une étiquette" desc="Sélectionnez un liquide, une taille de flacon et imprimez." />
        <ActionCell to="/history" testid="quick-history" icon={HistoryIcon} label="Historique de traçabilité" desc="Retrouvez tous les lots par date, client et quantité." />
      </div>
      <div className="grid grid-cols-12 gap-0 border-l border-r border-b border-zinc-800">
        <ActionCell to="/settings" testid="quick-settings" icon={SettingsIcon} label="Paramètres boutique" desc="Coordonnées, logo, format d'étiquette." />
        <ActionCell to="/registry" testid="quick-registry" icon={ClipboardList} label="Registre DGCCRF" desc="Export PDF/CSV de traçabilité obligatoire." />
        <ActionCell to="/credits" testid="quick-credits" icon={Coins} label="Recharger en crédits" desc="À partir de 0,10€ l'étiquette. Sans abonnement." />
      </div>

      {/* DLUO alerts */}
      <div className="mt-8 border border-zinc-800">
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="w-4 h-4 text-red-500" />
            <div className="font-display text-lg font-bold tracking-tight">Alertes DLUO (60 jours)</div>
          </div>
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500" data-testid="dluo-alerts-count">
            {dluo.expired_count > 0 && <span className="text-red-500 mr-2">{dluo.expired_count} expiré{dluo.expired_count > 1 ? "s" : ""} · </span>}
            {dluo.count} liquide{dluo.count > 1 ? "s" : ""}
          </div>
        </div>
        {dluo.alerts.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-zinc-500">
            Aucune DLUO proche. Tous vos liquides sont conformes ✓
          </div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {dluo.alerts.slice(0, 8).map((a) => (
              <div key={a.id} data-testid={`dluo-row-${a.id}`} className="px-6 py-3 flex items-center gap-4">
                <div className={`w-1.5 h-8 ${a.expired ? "bg-red-500" : a.days_left < 15 ? "bg-orange-500" : "bg-yellow-500"}`} />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-white">{a.brand} — {a.flavor}</div>
                  <div className="text-xs font-mono text-zinc-500">Lot {a.batch_number || "—"} · DLUO {a.dluo}</div>
                </div>
                <div className={`text-xs font-mono font-bold uppercase tracking-widest ${a.expired ? "text-red-500" : a.days_left < 15 ? "text-orange-400" : "text-yellow-400"}`}>
                  {a.expired ? `Expiré il y a ${-a.days_left} j` : `${a.days_left} j restants`}
                </div>
              </div>
            ))}
            {dluo.alerts.length > 8 && (
              <Link to="/liquids" className="block px-6 py-3 text-center text-xs font-mono text-zinc-400 hover:text-red-500">
                + {dluo.alerts.length - 8} autre{dluo.alerts.length - 8 > 1 ? "s" : ""} — voir dans la bibliothèque
              </Link>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function StatCell({ label, value, accent, testid }) {
  const c = { red: "text-red-500", green: "text-green-500", orange: "text-orange-400", white: "text-white" }[accent] || "text-white";
  return (
    <div className="col-span-12 md:col-span-4 p-8 border-b md:border-b-0 md:[&:not(:first-child)]:border-l border-zinc-800">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">{label}</div>
      <div className={`font-display text-5xl font-extrabold tracking-tighter mt-4 ${c}`} data-testid={testid}>{value}</div>
    </div>
  );
}

function ActionCell({ to, icon: Icon, label, desc, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className="col-span-12 md:col-span-4 p-8 border-t md:border-t-0 md:[&:not(:first-child)]:border-l border-zinc-800 hover:bg-zinc-900 transition-colors duration-150 group">
      <Icon className="w-5 h-5 text-red-500" strokeWidth={1.5} />
      <div className="font-display text-xl font-bold tracking-tight mt-4 group-hover:text-red-500 transition-colors">{label}</div>
      <div className="text-sm text-zinc-400 mt-2 leading-relaxed">{desc}</div>
    </Link>
  );
}
